import type { Node, Edge } from "@xyflow/react";
import type { WorkflowStep, ExecutionMode } from "@/types/workflow.types";
import type { WorkflowCanvasStepOverlay } from "./workflow-canvas-overlay.types";
import {
  HORIZONTAL_GAP,
  NODE_WIDTH,
  VIRTUAL_NODE_HEIGHT,
  VIRTUAL_NODE_WIDTH,
  buildDagLayout,
} from "./dag-layout.utils";

const LINEAR_STEPS_PER_ROW = 5;
const LINEAR_ESTIMATED_NODE_HEIGHT = 236;
const LINEAR_VERTICAL_GAP = 88;

export interface StepNodeData {
  step: WorkflowStep;
  connectorName?: string;
  connectorSubtype?: string;
  liveOverlay?: WorkflowCanvasStepOverlay;
  [key: string]: unknown;
}

export interface VirtualNodeData {
  kind: "start" | "end";
  label: string;
  [key: string]: unknown;
}

export type FlowNodeData = StepNodeData | VirtualNodeData;

/**
 * Convert WorkflowStep[] into XYFlow nodes and edges.
 * - Linear mode: horizontal ordering (Start -> step1 -> step2 -> End)
 * - DAG mode: uses valid uiPosition data; auto-layouts when persisted positions
 *   are incomplete or visually colliding.
 */
export function stepsToFlow(
  steps: WorkflowStep[],
  executionMode: ExecutionMode,
  connectorMap?: Record<string, { name: string; subtype: string }>,
  referenceLabels?: {
    models?: Record<string, string>;
    mcpTools?: Record<string, string>;
  },
  options?: {
    startLabel?: string;
  },
): { nodes: Node<FlowNodeData>[]; edges: Edge[] } {
  if (!steps || steps.length === 0) {
    return { nodes: [], edges: [] };
  }

  const sorted = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
  const dagLayout = executionMode === "dag" ? buildDagLayout(sorted) : undefined;
  const linearLayout = executionMode === "dag" ? undefined : buildLinearLayout(sorted.length);
  const stepNodes = sorted.map((step, index) =>
    createStepNode({
      step,
      index,
      executionMode,
      connector: step.connectorId ? connectorMap?.[step.connectorId] : undefined,
      position: dagLayout?.stepPositions[step.id] ?? linearLayout?.stepPositions[index],
      referenceLabels,
    }),
  );

  const edges =
    executionMode === "dag" ? createDagEdges(sorted, dagLayout) : createLinearEdges(sorted);

  const startPosition = dagLayout?.startPosition ?? linearLayout?.startPosition;
  const endPosition = dagLayout?.endPosition ?? linearLayout?.endPosition;
  const nodes: Node<FlowNodeData>[] =
    startPosition && endPosition
      ? [
          createVirtualNode("virtual-start", "start", startPosition, options?.startLabel),
          ...stepNodes,
          createVirtualNode("virtual-end", "end", endPosition),
        ]
      : stepNodes;

  return { nodes, edges };
}

function createStepNode(params: {
  step: WorkflowStep;
  index: number;
  executionMode: ExecutionMode;
  connector?: { name: string; subtype: string };
  position?: { x: number; y: number };
  referenceLabels?: {
    models?: Record<string, string>;
    mcpTools?: Record<string, string>;
  };
}): Node<StepNodeData> {
  const { step, index, executionMode, connector, referenceLabels } = params;
  const displayConfig = { ...step.config };
  const modelId = typeof displayConfig.model_id === "string" ? displayConfig.model_id : "";
  const toolId = typeof displayConfig.tool_id === "string" ? displayConfig.tool_id : "";
  if (modelId && referenceLabels?.models?.[modelId]) {
    displayConfig.model_display = referenceLabels.models[modelId];
  }
  if (toolId && referenceLabels?.mcpTools?.[toolId]) {
    displayConfig.tool_display = referenceLabels.mcpTools[toolId];
  }

  const fallbackPosition = {
    x: VIRTUAL_NODE_WIDTH + HORIZONTAL_GAP + index * (NODE_WIDTH + HORIZONTAL_GAP),
    y: 0,
  };

  return {
    id: step.id,
    type: "stepNode",
    position: params.position ?? fallbackPosition,
    data: {
      step: { ...step, config: displayConfig },
      connectorName: connector?.name,
      connectorSubtype: connector?.subtype,
    },
    width: NODE_WIDTH,
    draggable: executionMode === "dag",
  };
}

function createDagEdges(
  steps: WorkflowStep[],
  layout: ReturnType<typeof buildDagLayout> | undefined,
): Edge[] {
  if (!layout) return [];
  const edges: Edge[] = [
    ...layout.roots.map((root) => ({
      id: `virtual-start-${root.id}`,
      source: "virtual-start",
      target: root.id,
      type: "smoothstep",
      animated: false,
    })),
    ...layout.leaves.map((leaf) => ({
      id: `${leaf.id}-virtual-end`,
      source: leaf.id,
      target: "virtual-end",
      type: "smoothstep",
      animated: false,
    })),
  ];

  for (const step of steps) {
    for (const inputId of step.inputStepIds ?? []) {
      if (!layout.stepById.has(inputId)) continue;
      edges.push({
        id: `${inputId}-${step.id}`,
        source: inputId,
        target: step.id,
        type: "smoothstep",
        animated: false,
      });
    }
  }
  return edges;
}

function createLinearEdges(steps: WorkflowStep[]): Edge[] {
  if (steps.length === 0) return [];

  return [
    {
      id: `virtual-start-${steps[0].id}`,
      source: "virtual-start",
      target: steps[0].id,
      type: "smoothstep",
      animated: false,
    },
    ...steps.slice(0, -1).map((step, index) => ({
      id: `${step.id}-${steps[index + 1].id}`,
      source: step.id,
      target: steps[index + 1].id,
      type: "smoothstep",
      animated: false,
    })),
    {
      id: `${steps[steps.length - 1].id}-virtual-end`,
      source: steps[steps.length - 1].id,
      target: "virtual-end",
      type: "smoothstep",
      animated: false,
    },
  ];
}

function buildLinearLayout(stepCount: number): {
  stepPositions: Array<{ x: number; y: number }>;
  startPosition: { x: number; y: number };
  endPosition: { x: number; y: number };
} {
  const rowHeight = LINEAR_ESTIMATED_NODE_HEIGHT + LINEAR_VERTICAL_GAP;
  const stepPositions = Array.from({ length: stepCount }, (_, index) => {
    const row = Math.floor(index / LINEAR_STEPS_PER_ROW);
    const column = index % LINEAR_STEPS_PER_ROW;
    return {
      x: VIRTUAL_NODE_WIDTH + HORIZONTAL_GAP + column * (NODE_WIDTH + HORIZONTAL_GAP),
      y: row * rowHeight,
    };
  });
  const lastIndex = Math.max(0, stepCount - 1);
  const lastRow = Math.floor(lastIndex / LINEAR_STEPS_PER_ROW);
  const lastColumn = lastIndex % LINEAR_STEPS_PER_ROW;

  return {
    stepPositions,
    startPosition: { x: 0, y: 0 },
    endPosition: {
      x: VIRTUAL_NODE_WIDTH + HORIZONTAL_GAP + (lastColumn + 1) * (NODE_WIDTH + HORIZONTAL_GAP),
      y: lastRow * rowHeight,
    },
  };
}

function createVirtualNode(
  id: string,
  kind: VirtualNodeData["kind"],
  position: { x: number; y: number },
  label?: string,
): Node<VirtualNodeData> {
  return {
    id,
    type: "virtualNode",
    position,
    data: {
      kind,
      label: label ?? (kind === "start" ? "Start" : "End"),
    },
    width: VIRTUAL_NODE_WIDTH,
    height: VIRTUAL_NODE_HEIGHT,
    draggable: false,
    selectable: false,
  };
}
